import React, { useState, useEffect, useRef } from "react";
import { CCard, CCardHeader, CButton, CSpinner } from "@coreui/react";
import CIcon from "@coreui/icons-react";
import { cilMediaPlay, cilMediaPause, cilVolumeHigh, cilVolumeOff, cilFullscreen } from "@coreui/icons";
import ReactPlayer from "react-player";
import { formatDuration } from "../../utils/cameraUtils";

const CustomVideoPlayer = ({ url, initialSeekTime, seekTriggerId, title, externalPlaying, onTogglePlay }) => {
  const playerRef = useRef(null);
  const playerContainerRef = useRef(null);
  const [isReady, setIsReady] = useState(false);
  const [state, setState] = useState({ playing: false, volume: 0.8, muted: false, played: 0, loaded: 0, duration: 0, seeking: false });
  const isPlaying = externalPlaying !== undefined ? externalPlaying : state.playing;

  useEffect(() => {
    setIsReady(false);
    setState((prev) => ({ ...prev, played: 0, loaded: 0, duration: 0 }));
  }, [url]);

  useEffect(() => {
    let seekTimer;
    if (isReady && playerRef.current && initialSeekTime >= 0) {
      if (!onTogglePlay) setState((prev) => ({ ...prev, playing: false }));
      seekTimer = setTimeout(() => {
        try { playerRef.current.seekTo(initialSeekTime, "seconds"); } catch (e) {}
      }, 500);
    }
    return () => clearTimeout(seekTimer);
  }, [seekTriggerId, initialSeekTime, isReady, onTogglePlay]);

  const handlePlayPause = () => {
    if (!isReady) return;
    if (onTogglePlay) onTogglePlay(!isPlaying);
    else setState((prev) => ({ ...prev, playing: !prev.playing }));
  };

  const handleVolumeChange = (e) => setState((prev) => ({ ...prev, volume: parseFloat(e.target.value), muted: parseFloat(e.target.value) === 0 }));
  const handleToggleMuted = () => setState((prev) => ({ ...prev, muted: !prev.muted }));
  const handleProgress = (stateInfo) => { if (!state.seeking) setState((prev) => ({ ...prev, played: stateInfo.played, loaded: stateInfo.loaded })); };
  const handleDuration = (duration) => setState((prev) => ({ ...prev, duration }));
  const handleSeekMouseDown = () => { if (isReady) setState((prev) => ({ ...prev, seeking: true })); };
  const handleSeekChange = (e) => { if (isReady) setState((prev) => ({ ...prev, played: parseFloat(e.target.value) })); };
  
  const handleSeekMouseUp = (e) => {
    if (isReady) {
      setState((prev) => ({ ...prev, seeking: false }));
      if (playerRef.current) playerRef.current.seekTo(parseFloat(e.target.value));
    }
  };

  const handleClickFullscreen = () => {
    const el = playerContainerRef.current;
    if (!el) return;
    const isFullscreen = document.fullscreenElement || document.mozFullScreenElement || document.webkitFullscreenElement || document.msFullscreenElement;
    if (!isFullscreen) {
      if (el.requestFullscreen) el.requestFullscreen();
      else if (el.msRequestFullscreen) el.msRequestFullscreen();
      else if (el.mozRequestFullScreen) el.mozRequestFullScreen();
      else if (el.webkitRequestFullscreen) el.webkitRequestFullscreen(Element.ALLOW_KEYBOARD_INPUT);
    } else {
      if (document.exitFullscreen) document.exitFullscreen();
      else if (document.msExitFullscreen) document.msExitFullscreen();
      else if (document.mozCancelFullScreen) document.mozCancelFullScreen();
      else if (document.webkitExitFullscreen) document.webkitExitFullscreen();
    }
  };

  const currentTime = state.duration * state.played;

  return (
    <CCard className="h-100 w-100 m-0 border-secondary d-flex flex-column rounded-0 border-0" style={{ overflow: "hidden" }}>
      {title && <CCardHeader className="bg-dark text-white py-1 small flex-shrink-0">{title}</CCardHeader>}
      <div ref={playerContainerRef} className="position-relative bg-black flex-grow-1" style={{ width: "100%", minHeight: 0, display: "flex", justifyContent: "center", alignItems: "center" }}>
        {!isReady && <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", zIndex: 10 }}><CSpinner color="light" /></div>}
        <ReactPlayer
          ref={playerRef} className="react-player" width="100%" height="100%" url={url} playing={isPlaying} controls={false}
          volume={state.volume} muted={state.muted} onReady={() => setIsReady(true)} onProgress={handleProgress} onDuration={handleDuration}
          onEnded={() => { if (onTogglePlay) onTogglePlay(false); else setState((prev) => ({ ...prev, playing: false })); }}
          config={{ file: { attributes: { controlsList: "nodownload", crossOrigin: "anonymous" } } }}
        />
        <div className="custom-controls p-2" style={{ position: "absolute", bottom: 0, left: 0, right: 0, background: "rgba(0,0,0,0.7)", display: "flex", flexDirection: "column", gap: "5px", opacity: isReady ? 1 : 0.5, pointerEvents: isReady ? "auto" : "none" }}>
          <div className="d-flex align-items-center w-100">
            <input type="range" min={0} max={0.999999} step="any" value={state.played} disabled={!isReady} onMouseDown={handleSeekMouseDown} onChange={handleSeekChange} onMouseUp={handleSeekMouseUp} style={{ width: "100%", cursor: isReady ? "pointer" : "not-allowed", height: "5px", accentColor: "#321fdb" }} />
          </div>
          <div className="d-flex justify-content-between align-items-center text-white">
            <div className="d-flex align-items-center gap-3">
              <CButton color="link" className="text-white p-0" onClick={handlePlayPause} disabled={!isReady}><CIcon icon={isPlaying ? cilMediaPause : cilMediaPlay} size="lg" /></CButton>
              <div className="d-flex align-items-center gap-2">
                <CButton color="link" className="text-white p-0" onClick={handleToggleMuted} disabled={!isReady}><CIcon icon={state.muted || state.volume === 0 ? cilVolumeOff : cilVolumeHigh} /></CButton>
                <input type="range" min={0} max={1} step="any" value={state.muted ? 0 : state.volume} onChange={handleVolumeChange} disabled={!isReady} style={{ width: "80px", accentColor: "#fff", height: "3px", cursor: isReady ? "pointer" : "not-allowed" }} />
              </div>
              <span className="small font-monospace">{formatDuration(currentTime)} / {formatDuration(state.duration)}</span>
            </div>
            <CButton color="link" className="text-white p-0" onClick={handleClickFullscreen} disabled={!isReady}><CIcon icon={cilFullscreen} /></CButton>
          </div>
        </div>
      </div>
    </CCard>
  );
};
export default CustomVideoPlayer;